import { normalizePalette } from 'api/merchantTerminalThemes/normalizers';
import { TLayoutsKeys, TPermanentPalette } from 'api/merchantTerminalThemes/types';
import { TObject } from 'utils/types';

import { IHostedFieldsMerchant, IHostedFieldsTheme } from './types';

type IHFThemeData = TObject & {
  palette?: TObject;
  permanentPalette?: TObject;
};

type IHFThemePayloadData = TObject & {
  palette?: string | TObject[];
  permanentPalette?: string | TObject;
};

export const normalizeHFTheme = (theme: IHFThemePayloadData): IHostedFieldsTheme => {
  let data: IHFThemeData;
  try {
    data = JSON.parse((theme?.theme as string) || '');
  } catch {
    data = {} as IHFThemeData;
  }

  return {
    createdAt: (theme?.createdAt as string) || '',
    id: (theme?.id as string) || '',
    isAnimatedLabel: (theme?.preview as boolean) ?? true,
    isSystem: theme?.merchantTerminalId === 'system',
    layoutName: (data?.layoutName as TLayoutsKeys) || 'hostedFieldsLayout',
    name: (theme?.name as string) || '',
    palette:
      typeof data?.palette === 'string'
        ? data?.palette
        : (data?.palette as unknown as TObject[])?.map(normalizePalette) || [],
    permanentPalette:
      typeof data?.permanentPalette === 'string'
        ? data?.permanentPalette
        : (data?.permanentPalette as TPermanentPalette) || {},
    preview: (theme?.preview as string) || '',
    theme: (data?.theme as TObject) || {},
    updatedAt: (theme?.updatedAt as string) || '',
  };
};

const isPlainObject = (value: unknown): value is TObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyObject = (value: unknown): value is TObject =>
  isPlainObject(value) && Object.keys(value).length > 0;

// The editor requires the parsed theme blob to carry the components tree
// (e.g. theme.components.typography) — an empty or unparsable stored theme
// yields `{}` from normalizeHFTheme and would crash the editor
export const isValidHFTheme = (theme: IHostedFieldsTheme): boolean =>
  isNonEmptyObject(theme.theme) && isPlainObject(theme.theme.components);

type THFMerchantData = TObject & {
  themes?: IHFThemePayloadData[];
};

export const normalizeHFMerchant = (data: THFMerchantData): IHostedFieldsMerchant => {
  return {
    activeHostedFieldsTheme: (data?.activeHostedFieldsTheme as string) || '',
    themes: data?.themes?.map(normalizeHFTheme) || [],
  };
};

export const getSortedHFThemes = (
  themes: IHostedFieldsTheme[],
  activeTheme: string,
): IHostedFieldsTheme[] => {
  const sortThemes = (themes: IHostedFieldsTheme[]) =>
    themes.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  const active: IHostedFieldsTheme[] = [];
  const system: IHostedFieldsTheme[] = [];
  const custom: IHostedFieldsTheme[] = [];

  themes.forEach((theme) => {
    if (theme.id === activeTheme) {
      active.push(theme);
    } else if (theme.isSystem) {
      system.push(theme);
    } else {
      custom.push(theme);
    }
  });

  return active.concat(sortThemes(system), sortThemes(custom));
};
